import { ReactSVG } from "react-svg";
import { getExpoList } from "../utils/helpers.ts";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import React from "react";

type Altitude = number | "treeline" | null | undefined;

function getAltitudeData(range: [Altitude, Altitude]): [string[], string] {
  const [from, to] = range;
  let description = "";
  let layer: string[] = [];

  if (from && to) {
    layer = ["middle"];
    description = `Von ${from}m bis ${to}m`;
  } else if (from) {
    layer = ["top"];
    description = `Über ${from}m`;
    if (from == "treeline") {
      description = `Ab Baumgrenze`;
    }
  } else if (to) {
    layer = ["bottom"];
    description = `Unter ${to}m`;
    if (to == "treeline") {
      description = `Bis Baumgrenze`;
    }
  } else {
    layer = ["bottom", "middle", "top"];
    description = "Alle Höhenlagen";
  }
  return [layer, description];
}

function getExposures(expo?: string[] | null): string[] {
  if (!expo) {
    return [];
  }

  // Handle "all" to return all directions
  if (expo.includes("all")) {
    return ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  }
  // Define the order of directions for interpolation
  const allDirections = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
  // Helper function to get all directions between two
  function getRange(start: string, end: string) {
    const startIndex = allDirections.indexOf(start as any);
    const endIndex = allDirections.indexOf(end as any);
    if (startIndex === -1 || endIndex === -1) {
      return [];
    }
    // If the range wraps around (e.g., NW to NE)
    if (endIndex < startIndex) {
      return allDirections
        .slice(startIndex)
        .concat(allDirections.slice(0, endIndex + 1));
    }
    // Normal range (e.g., N to S)
    return allDirections.slice(startIndex, endIndex + 1);
  }

  // Process exposures
  const individualExposures = new Set<string>();
  expo.forEach((e) => {
    if (e.includes("_")) {
      // Split and get range for combined exposures
      const [start, end] = e.split("_");
      getRange(start, end).forEach((dir) => individualExposures.add(dir));
    } else if (
      (["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const).includes(e as any)
    ) {
      // Add single exposure
      individualExposures.add(e);
    }
  });

  // Return unique exposures as an array
  return Array.from(individualExposures);
}

const defaultOpacities: Record<"top" | "middle" | "bottom", string> = {
  top: "1.0",
  middle: "0.95",
  bottom: "0.90",
};

const defaultColors: Record<"flat" | "side", string> = {
  flat: "#ddcfe2",
  side: "#bb9ec5",
};

function deactivateLayer(cssId: string) {
  const elementsDel = document.querySelectorAll("." + cssId);
  elementsDel.forEach((element) => {
    element.classList.remove(cssId);
  });
}

function switchLayer(cssId: string) {
  const elements = document.querySelectorAll("." + cssId);
  elements.forEach((element) => {
    const regex = /^mlr_(top|middle|bottom)_(N|E|S|W|NE|NW|SE|SW)(_1|_2)?-\d+$/;
    const match = (element as HTMLElement).id.match(regex);
    if (match) {
      const res = {
        layer: match[1] as "top" | "middle" | "bottom", // top, middle oder bottom
        direction: match[2] as
          | "N"
          | "E"
          | "S"
          | "W"
          | "NE"
          | "NW"
          | "SE"
          | "SW", // N, E, S, W, NE, NW, SE, SW
        face: match[3] ? ("side" as const) : ("flat" as const), // _1, _2 oder null, falls nicht vorhanden
      };
      const className = "multirose-" + res.face + "-" + res.layer;
      if (element.classList.contains(className)) {
        element.classList.remove(className);
      } else {
        element.classList.add(className);
      }
    }
  });
}

interface MultiLevelRoseProps {
  faceColorStyle?: Record<"flat" | "side", string>;
  faceOpacityStyle?: Record<"top" | "middle" | "bottom", string>;
  width?: string;
  height?: string;
  conditionData: Array<[Altitude, Altitude, any, boolean]>;
  cssIdentifier: string;
  noHeight?: boolean;
}

const MultiLevelRose: React.FC<MultiLevelRoseProps> = React.memo(
  (props: MultiLevelRoseProps) => {
    const faceColorStyle = props.faceColorStyle
      ? props.faceColorStyle
      : defaultColors;
    const faceOpacityStyle = props.faceOpacityStyle
      ? props.faceOpacityStyle
      : defaultOpacities;

    const wd = props.width ? props.width : "50px";
    const he = props.height ? props.height : "50px";
    const regex: RegExp =
      /^mlr_(top|middle|bottom)_(N|E|S|W|NE|NW|SE|SW)(_1|_2)?-\d+$/;

    return (
      <>
        <div
          style={{
            justifyContent: "center",
            width: "100%",
            display: "flex",
            alignItems: "center",
            fontFamily: "Ubuntu",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <div style={{ margin: "10px" }}>
            <ReactSVG
              src="../../multi_level_rose.svg"
              afterInjection={(svg: SVGElement) => {
                svg.setAttribute("width", wd);
                svg.setAttribute("height", he);
                (
                  svg.querySelectorAll("[id]") as NodeListOf<HTMLElement>
                ).forEach((element) => {
                  const id = element.id;
                  const match = id.match(regex);
                  if (match) {
                    const res = {
                      layer: match[1] as "top" | "middle" | "bottom", // top, middle oder bottom
                      direction: match[2] as
                        | "N"
                        | "E"
                        | "S"
                        | "W"
                        | "NE"
                        | "NW"
                        | "SE"
                        | "SW", // N, E, S, W, NE, NW, SE, SW
                      face: match[3] ? ("side" as const) : ("flat" as const), // _1, _2 oder null, falls nicht vorhanden
                    };
                    for (const idx in props.conditionData) {
                      const [
                        fromAltitude,
                        toAltitude,
                        expo,
                        draw_middle_layer,
                      ] = props.conditionData[idx];
                      const exposures = getExposures(getExpoList(expo));
                      let altData = getAltitudeData([fromAltitude, toAltitude]);
                      if (draw_middle_layer && !altData[0].includes("middle")) {
                        altData[0].push("middle");
                      }
                      if (
                        altData[0].includes(res.layer) &&
                        exposures.includes(res.direction)
                      ) {
                        const opac = faceOpacityStyle[res.layer];
                        const color = faceColorStyle[res.face];
                        element.setAttribute("fill", color);
                        element.setAttribute("opacity", opac);
                        element.classList.add(props.cssIdentifier + "-" + idx);
                      }
                    }
                  }
                });
              }}
            />
          </div>
          {!props.noHeight && (
          <div style={{ textAlign: "left", width: "60%", marginLeft: "20px" }}>
            <FormControl component="fieldset" variant="standard">
              <FormGroup>
                {props.conditionData.map(
                  (
                    [fromAltitude, toAltitude, expo, draw_middle_layer],
                    idx
                  ) => (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled
                            size="small"
                            onChange={() =>
                              switchLayer(props.cssIdentifier + "-" + idx)
                            }
                            icon={<ArrowRightIcon />}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              textAlign: "left",
                              marginTop: "2px",
                              fontSize: 14,
                              fontFamily: "Ubuntu",
                            }}
                          >
                            {getAltitudeData([fromAltitude, toAltitude])[1]}
                          </Typography>
                        }
                      />
                    </>
                  )
                )}
              </FormGroup>
            </FormControl>
          </div>)}
        </div>
      </>
    );
  }
);

export default MultiLevelRose;
